
import React, { useEffect, useState } from "react";
import "./posReceiptTemplateStyle.css";
import { useSelector } from "react-redux";
import language from "../../i18n/languages.json";
import { convertToArabicMobileNo, convertToArabicNumber, numToArabicWords, numToWords } from "../../../../../Js/generalFunctions";
import Barcode from "react-barcode";
import QRCode from "react-qr-code";
import { convertDateFormat, convertToAMPM } from "../../../../../Js/Date";
import CachedImage from "../../../../../offline/components/CachedImage";

const PosReceiptTemplate = (props) => {
    const body = {
      query: "good!",
      sourceLang: language.English,
      targetLang: language.Arabic,
    };

    // Helper to safely convert orderDate to string (handles Date objects from IndexedDB)
    const getOrderDateString = (orderDate) => {
      if (!orderDate) return '';
      if (orderDate instanceof Date) {
        // Format as "YYYY-MM-DD HH:mm:ss"
        const pad = (n) => n.toString().padStart(2, '0');
        return `${orderDate.getFullYear()}-${pad(orderDate.getMonth() + 1)}-${pad(orderDate.getDate())} ${pad(orderDate.getHours())}:${pad(orderDate.getMinutes())}:${pad(orderDate.getSeconds())}`;
      }
      return typeof orderDate === 'string' ? orderDate : String(orderDate);
    };
 
    const decimalPosition = localStorage.getItem("decimalPosition");
    const { posTemplateSelected } = useSelector((state) => state.posReceiptSlice);
    const { receiptData: rawReceiptData } = useSelector((state) => state.viewOrdersBillingSlice);
    const companyProfileData = useSelector((state) => state?.companyProfileSlice?.userValue);
    const branchSettingsData = useSelector((state) => state?.branchSettingsSliceNew?.rowData);

    // Track imageUrl map version to force re-enrichment
    const [imgMapVersion, setImgMapVersion] = useState(0);
    useEffect(() => {
      const handler = () => setImgMapVersion((v) => v + 1);
      window.addEventListener('receiptImageMapUpdated', handler);
      return () => window.removeEventListener('receiptImageMapUpdated', handler);
    }, []);

    // Enrich receiptData.orderInfo with imageUrl from window.__receiptImageUrlMap (set by BillingNew)
    // Backend now returns imageUrl; imgMap is a fallback for older orders without it
    const receiptData = React.useMemo(() => {
      if (!rawReceiptData) return null;
      const imgMap = window.__receiptImageUrlMap || {};
      return {
        ...rawReceiptData,
        orderInfo: (rawReceiptData.orderInfo || []).map((item) => ({
          ...item,
          imageUrl: item.imageUrl || imgMap[item.productId] || imgMap[item._id] || null,
        })),
      };
    }, [rawReceiptData, imgMapVersion]);
    
    // Terms & Conditions priority: branch settings → receiptData → companyProfile
    const resolvedTermsAndConditions = branchSettingsData?.termsAndCondition || receiptData?.termsAndConditions || companyProfileData?.termsAndConditions || "";
    
    // Font selection from Device Settings
    const selectedFont = localStorage.getItem('posReceiptFont') || 'Arial';
    
    // Get template from Redux first, fallback to localStorage for offline mode
    const getInitialTemplate = () => {
      // Allow callers (e.g. self-order) to force a specific template design
      if (props?.templateOverride) return Number(props.templateOverride);
      const reduxTemplate = posTemplateSelected?.posReceiptTemplate;
      if (reduxTemplate) return Number(reduxTemplate);
      const localTemplate = localStorage.getItem('posReceiptTemplate');
      console.log('🧾 PosReceiptTemplate: Using localStorage template for offline:', localTemplate);
      return localTemplate ? Number(localTemplate) : 1; // Default to template 1
    };
    
    const [tempalteType, setTemplateType] = useState(getInitialTemplate);
    const [domain, setDomain] = useState(null);
 
    const { dialogOpen } = useSelector((state) => state.billingScreenSlice);
    const posSettings = useSelector((state) => state.posSettingSlice.apiResList);

    // Debug logs
    useEffect(() => {
      console.log("PosReceiptTemplate MOUNTED");
      return () => console.log("PosReceiptTemplate UNMOUNTED");
    }, []);

    // When a template override is supplied, keep it fixed regardless of settings
    useEffect(() => {
      if (props?.templateOverride) {
        setTemplateType(Number(props.templateOverride));
      }
    }, [props?.templateOverride]);

    // Update template when Redux changes OR fallback to localStorage
    useEffect(() => {
      if (props?.templateOverride) return; // override wins
      const reduxTemplate = posTemplateSelected?.posReceiptTemplate;
      const localTemplate = localStorage.getItem('posReceiptTemplate');
      
      if (reduxTemplate) {
        console.log('🧾 PosReceiptTemplate: Using Redux template:', reduxTemplate);
        setTemplateType(Number(reduxTemplate));
      } else if (localTemplate) {
        console.log('🧾 PosReceiptTemplate: Redux empty, using localStorage template:', localTemplate);
        setTemplateType(Number(localTemplate));
      }
    }, [posTemplateSelected]);

    // Also sync template from localStorage on every receiptData change (covers socket print)
    useEffect(() => {
      if (props?.templateOverride) return; // override wins
      const localTemplate = localStorage.getItem('posReceiptTemplate');
      if (localTemplate && Number(localTemplate) !== tempalteType) {
        console.log('🧾 PosReceiptTemplate: Syncing template from localStorage:', localTemplate);
        setTemplateType(Number(localTemplate));
      }
    }, [receiptData]);

    // Listen for forced template sync events (triggered before socket print)
    useEffect(() => {
      const handleForceSync = () => {
        if (props?.templateOverride) return; // override wins
        const localTemplate = localStorage.getItem('posReceiptTemplate');
        if (localTemplate) {
          console.log('🧾 PosReceiptTemplate: Force syncing template from localStorage:', localTemplate);
          setTemplateType(Number(localTemplate));
        }
      };
      window.addEventListener('forceTemplateSync', handleForceSync);
      return () => window.removeEventListener('forceTemplateSync', handleForceSync);
    }, []);

    useEffect(() => {
      console.log("PosReceiptTemplate - receiptData changed:", receiptData);
      console.log("🎨 tempalteType:", tempalteType, "typeof:", typeof tempalteType, "isItemsImage:", tempalteType === 10);
      if (receiptData) {
        console.log("Receipt has orderInfo:", receiptData.orderInfo);
        console.log("Receipt orderInfo[0]:", receiptData.orderInfo?.[0]);
        console.log("🖼️ orderInfo imageUrls:", (receiptData.orderInfo || []).map(item => ({ name: item?.productName, imageUrl: item?.imageUrl })));
        console.log("Receipt logo:", receiptData.logo);
        console.log("Redux companyProfile logo:", companyProfileData?.logo);
      }
    }, [receiptData, companyProfileData, tempalteType]);
 
    // Calculate items per page based on template type
    const itemsPerPage = tempalteType === 4 ? 20 : 15;
    const totalItems = receiptData?.orderInfo?.length || 0;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
 
    // Determine printer width from device settings (defaults to 80mm)
    const printerSettings = (() => {
      try { return JSON.parse(localStorage.getItem('PrinterSettings') || '{}'); } catch (_) { return {}; }
    })();
    // Map to printable width (80->72, 58->48) and subtract 2mm safety to avoid clipping
    const printableWidthMM = printerSettings?.receiptSize === '58' ? 48 : 72;
    const containerWidth = `${Math.max(40, printableWidthMM - 2)}mm`;

    // Template-specific configuration
    const isCompact = tempalteType === 5;
    const isPremium = tempalteType === 6;
    const isArabicPremium = tempalteType === 7;
    const isSpaceSaver = tempalteType === 8;
    const isFormat4 = tempalteType === 9;
    const isItemsImage = tempalteType === 10;
    const isTokenStyle = tempalteType === 11;
    
    const templateConfig = {
        containerClass: tempalteType === 4 ? 'template-three-continer' : (isCompact ? 'template-compact' : (isPremium || isArabicPremium ? 'template-premium' : (isSpaceSaver ? 'template-space-saver' : ((isFormat4 || isItemsImage) ? 'template-format4' : (isTokenStyle ? 'template-token-style' : ''))))),
        // Always fix the receipt container width to the printable width minus small margin
        containerStyle: { 
          width: containerWidth, 
          margin: '0 auto',
          fontFamily: `'${selectedFont}', sans-serif`,
          ...(isCompact && { fontSize: '9px', lineHeight: '1.1' }),
          ...(isPremium && {}),
          ...(isArabicPremium && { 
            fontFamily: `'${selectedFont}', 'Simplified Arabic', sans-serif`, 
            direction: 'rtl',
            unicodeBidi: 'embed',
            letterSpacing: 'normal',
            wordSpacing: 'normal'
          }),
          ...(isSpaceSaver && { fontSize: '12px', lineHeight: '1.3' }),
          ...(isFormat4 && { fontSize: '12px', lineHeight: '1.3', padding: '0px 0.4em 0' }),
          ...(isItemsImage && { fontSize: '12px', lineHeight: '1.3', padding: '0px 0.4em 0' }),
          ...(isTokenStyle && { fontSize: '11px', lineHeight: '1.2', padding: '0px 0.3em 0' })
        },
        tableStyle: tempalteType === 4 ? {
            fontSize: '11px',
            width: '100%',
            tableLayout: 'fixed',
            borderCollapse: 'collapse'
        } : isCompact ? {
            fontSize: '8px',
            width: '100%',
            borderCollapse: 'collapse',
            lineHeight: '1'
        } : isPremium ? {
            fontSize: '11px',
            width: '100%',
            borderCollapse: 'collapse',
            borderSpacing: '0'
        } : (isSpaceSaver || isFormat4 || isItemsImage) ? {
            fontSize: '12px',
            width: '100%',
            borderCollapse: 'collapse'
        } : isTokenStyle ? {
            fontSize: '11px',
            width: '100%',
            borderCollapse: 'collapse'
        } : {
            width: '100%',
            // tableLayout: 'fixed',
            borderCollapse: 'collapse'
        },
        cellStyle: tempalteType === 4 ? {
            padding: '2px 4px',
            overflow: 'hidden',
            wordWrap: 'break-word'
        } : isCompact ? {
            padding: '1px 2px',
            overflow: 'hidden',
            wordWrap: 'break-word',
            lineHeight: '1'
        } : isPremium ? {
            padding: '4px 6px',
            overflow: 'hidden',
            wordWrap: 'break-word'
        } : (isSpaceSaver || isFormat4 || isItemsImage) ? {
            padding: '1px 2px',
            overflow: 'hidden',
            wordWrap: 'break-word'
        } : isTokenStyle ? {
            padding: '2px 3px',
            overflow: 'hidden',
            wordWrap: 'break-word'
        } : {
            padding: '2px 4px',
            overflow: 'hidden',
            wordWrap: 'break-word'
        },
        headerStyle: tempalteType === 4 ? { fontSize: '0.9em' } : (isCompact ? { fontSize: '0.7em' } : (isPremium ? { fontSize: '1.1em', fontWeight: '600' } : ((isSpaceSaver || isFormat4 || isItemsImage || isTokenStyle) ? { fontSize: '1em', fontWeight: '700' } : {}))),
        textStyle: tempalteType === 4 ? { fontSize: '0.75em' } : (isCompact ? { fontSize: '0.65em', lineHeight: '1' } : (isPremium ? { fontSize: '0.85em' } : ((isSpaceSaver || isFormat4 || isItemsImage || isTokenStyle) ? { fontSize: '0.95em' } : {})))
    };
 
    useEffect(() => {
      if (receiptData !== null) {
        const domainAddress = `${window.location.protocol}//${window.location.host}/posReaderPreview?path=admin/viewqrcodedata&db=${encodeURIComponent(receiptData?.db)}&vendororcusid=${encodeURIComponent(receiptData?.cusId)}&type=${encodeURIComponent("POS")}&orderId=${encodeURIComponent(receiptData?._id)}`;
        setDomain(domainAddress);
      }
    }, [receiptData]);
 
    useEffect(() => {
      console.log('🧾 PosReceiptTemplate - posTemplateSelected changed:', posTemplateSelected);
      setTemplateType(posTemplateSelected?.posReceiptTemplate);
    }, [posTemplateSelected]);

    useEffect(() => {
      console.log('🧾 PosReceiptTemplate - templateType changed to:', tempalteType);
    }, [tempalteType]);

    // Early return if no receipt data - prevents errors in render functions
    if (!receiptData) {
      return null;
    }
 
    const renderHeader = () => {
      // Use data URL if available (for offline), otherwise use regular URL
      const logoSrc = window.__receiptLogoDataUrl || companyProfileData?.logo || receiptData?.logo;
      
      // Compact template - minimal header
      if (isCompact) {
        return (
          <div className="pos-receipt-template-header" style={{ padding: '2px 0', marginBottom: '2px' }}>
            <div className="template-header-company-info" style={{ gap: '0' }}>
              <span className="template-restaurant-name" style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '1px' }}>
                {receiptData?.nameOfStore || props?.nameOfStore}
              </span>
              <span style={{ fontSize: '7px' }}>{receiptData?.contactNumber || props?.contactNumber || ""}</span>
              {(receiptData?.trnNumber || props?.trnNumber) && (
                <span style={{ fontSize: '7px' }}>TRN: {receiptData?.trnNumber || props?.trnNumber}</span>
              )}
            </div>
          </div>
        );
      }

      // Space Saver template (8) - logo, phone, location
      if (isSpaceSaver) {
        const branchLocation = receiptData?.address || companyProfileData?.companyAddress || companyProfileData?.profile?.companyAddress || "";
        return (
          <div className="pos-receipt-template-header" style={{ padding: '4px 0', textAlign: 'center' }}>
            {logoSrc && (
              <div className="template-header-company-logo" style={{ marginBottom: '4px' }}>
                {window.__receiptLogoDataUrl ? (
                  <img src={logoSrc} alt="logo" style={{ maxHeight: '40px' }} onError={(e) => { if (e?.target?.parentElement) e.target.parentElement.style.display = "none"; }} crossOrigin="anonymous" />
                ) : (
                  <CachedImage src={logoSrc} alt="logo" style={{ maxHeight: '40px' }} onError={(e) => { if (e?.target?.parentElement) e.target.parentElement.style.display = "none"; }} />
                )}
              </div>
            )}
            <div className="template-header-company-info" style={{ gap: '1px' }}>
              <span style={{ fontSize: '10px', fontWeight: '700' }}>Ph: {receiptData?.contactNumber || props?.contactNumber || ""}</span>
              {branchLocation && <span style={{ fontSize: '10px', fontWeight: '700' }}>{branchLocation}</span>}
            </div>
          </div>
        );
      }

      // Token Style template (11) - centered header with logo, company name, address, contact, GST, date
      if (isTokenStyle) {
        const branchLocation = receiptData?.address || companyProfileData?.companyAddress || companyProfileData?.profile?.companyAddress || "";
        const companyName = receiptData?.nameOfStore || companyProfileData?.companyName || companyProfileData?.profile?.companyName || "";
        return (
          <div className="pos-receipt-template-header" style={{ padding: '4px 0', textAlign: 'center' }}>
            {logoSrc && (
              <div className="template-header-company-logo" style={{ marginBottom: '6px' }}>
                {window.__receiptLogoDataUrl ? (
                  <img src={logoSrc} alt="logo" style={{ maxHeight: '45px' }} onError={(e) => { if (e?.target?.parentElement) e.target.parentElement.style.display = "none"; }} crossOrigin="anonymous" />
                ) : (
                  <CachedImage src={logoSrc} alt="logo" style={{ maxHeight: '45px' }} onError={(e) => { if (e?.target?.parentElement) e.target.parentElement.style.display = "none"; }} />
                )}
              </div>
            )}
            <div className="template-header-company-info" style={{ gap: '2px' }}>
              {companyName && <span style={{ fontSize: '13px', fontWeight: '700', textTransform: 'uppercase' }}>{companyName}</span>}
              <span style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase' }}>{branchLocation}</span>
              <span style={{ fontSize: '10px' }}>Contact No: {receiptData?.contactNumber || props?.contactNumber || ""}</span>
              {(receiptData?.trnNumber || props?.trnNumber) && (
                <span style={{ fontSize: '10px' }}>GST No.: {receiptData?.trnNumber || props?.trnNumber}</span>
              )}
            </div>
          </div>
        );
      }

      // Format 4 / Items Image template (9/10) - logo, phone, location
      if (isFormat4 || isItemsImage) {
        const branchLocation = receiptData?.address || companyProfileData?.companyAddress || companyProfileData?.profile?.companyAddress || "";
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0', margin: '0', textAlign: 'center' }}>
            {logoSrc && (
              <div style={{ margin: '0', padding: '0', overflow: 'hidden', width: '12.5em', lineHeight: '0' }}>
                {window.__receiptLogoDataUrl ? (
                  <img src={logoSrc} alt="logo" style={{ maxHeight: '40px', width: '100%', objectFit: 'contain', display: 'block' }} onError={(e) => { if (e?.target?.parentElement) e.target.parentElement.style.display = "none"; }} crossOrigin="anonymous" />
                ) : (
                  <CachedImage src={logoSrc} alt="logo" style={{ maxHeight: '40px', width: '100%', objectFit: 'contain', display: 'block' }} onError={(e) => { if (e?.target?.parentElement) e.target.parentElement.style.display = "none"; }} />
                )}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0px', margin: '0', padding: '0' }}>
              <span style={{ fontSize: '10px', fontWeight: '700', lineHeight: '1.2' }}>Ph: {receiptData?.contactNumber || props?.contactNumber || ""}</span>
              {branchLocation && <span style={{ fontSize: '10px', fontWeight: '700', lineHeight: '1.2' }}>{branchLocation}</span>}
            </div>
          </div>
        );
      }
      
      // Arabic Premium template - RTL elegant header
      if (isArabicPremium) {
        return (
          <div className="pos-receipt-template-header" style={{ 
            padding: '8px 0', 
            borderBottom: '2px solid #333',
            marginBottom: '8px',
            direction: 'rtl',
            textAlign: 'center'
          }}>
            {logoSrc && (
              <div className="template-header-company-logo" style={{ marginBottom: '6px' }}>
                {window.__receiptLogoDataUrl ? (
                  <img src={logoSrc} alt="logo" style={{ maxHeight: '50px' }} onError={(e) => { if (e?.target?.style) e.target.style.display = "none"; }} crossOrigin="anonymous" />
                ) : (
                  <CachedImage 
                    src={logoSrc} 
                    alt="logo" 
                    style={{ maxHeight: '50px' }}
                    onError={(e) => { if (e?.target?.style) e.target.style.display = "none"; }}
                  />
                )}
              </div>
            )}
            <div className="template-header-company-info" style={{ gap: '2px' }}>
              <span className="template-restaurant-name" style={{ 
                fontSize: '14px', 
                fontWeight: '700', 
                marginBottom: '4px'
              }}>
                {receiptData?.nameOfStore || props?.nameOfStore}
              </span>
              <span style={{ fontSize: '9px' }}>{receiptData?.address || ""}</span>
              <span style={{ fontSize: '9px' }}>{receiptData?.contactNumber ? convertToArabicMobileNo(receiptData?.contactNumber) : ""}</span>
              {(receiptData?.trnNumber || props?.trnNumber) && (
                <span style={{ fontSize: '9px', fontWeight: '600', marginTop: '2px' }}>
                  الرقم الضريبي: {convertToArabicNumber(receiptData?.trnNumber || props?.trnNumber)}
                </span>
              )}
            </div>
          </div>
        );
      }
      
      // Premium template - elegant header with border
      if (isPremium) {
        return (
          <div className="pos-receipt-template-header" style={{ 
            padding: '8px 0', 
            borderBottom: '2px solid #333',
            marginBottom: '8px'
          }}>
            {logoSrc && (
              <div className="template-header-company-logo" style={{ marginBottom: '6px' }}>
                {window.__receiptLogoDataUrl ? (
                  <img src={logoSrc} alt="logo" style={{ maxHeight: '50px' }} onError={(e) => { if (e?.target?.style) e.target.style.display = "none"; }} crossOrigin="anonymous" />
                ) : (
                  <CachedImage 
                    src={logoSrc} 
                    alt="logo" 
                    style={{ maxHeight: '50px' }}
                    onError={(e) => { if (e?.target?.style) e.target.style.display = "none"; }}
                  />
                )}
              </div>
            )}
            <div className="template-header-company-info" style={{ gap: '2px' }}>
              <span className="template-restaurant-name" style={{ 
                fontSize: '14px', 
                fontWeight: '700', 
                letterSpacing: '1px',
                textTransform: 'uppercase',
                marginBottom: '4px'
              }}>
                {receiptData?.nameOfStore || props?.nameOfStore}
              </span>
              <span style={{ fontSize: '9px', fontStyle: 'italic' }}>{receiptData?.address || ""}</span>
              <span style={{ fontSize: '9px' }}>{receiptData?.contactNumber || props?.contactNumber || ""}</span>
              {(receiptData?.trnNumber || props?.trnNumber) && (
                <span style={{ fontSize: '9px', fontWeight: '600', marginTop: '2px' }}>
                  TRN: {receiptData?.trnNumber || props?.trnNumber}
                </span>
              )}
            </div>
          </div>
        );
      }
      
      // Default templates
      return (
      <div className="pos-receipt-template-header">
        {logoSrc && (
          <div className="template-header-company-logo">
            {window.__receiptLogoDataUrl ? (
              <img src={logoSrc} alt="logo" onError={(e) => { if (e?.target?.style) e.target.style.display = "none"; }} crossOrigin="anonymous" />
            ) : (
              <CachedImage 
                src={logoSrc} 
                alt="logo" 
                onError={(e) => { if (e?.target?.style) e.target.style.display = "none"; }}
              />
            )}
          </div>
        )}
        <div className="template-header-company-info">
          <span className="template-restaurant-name" style={templateConfig.headerStyle}>
            {receiptData?.nameOfStore || props?.nameOfStore}
          </span>
          <span style={{ textAlign: "center" }}>{""} </span>
          <span style={templateConfig.textStyle}>{receiptData?.email || props?.email || "---"} </span>
          <span style={templateConfig.textStyle}>{receiptData?.contactNumber || props?.contactNumber || "---"} </span>
          {(receiptData?.trnNumber || props?.trnNumber) && (
            <span className="template-transaction-number" style={templateConfig.textStyle}>
              TRN : {receiptData?.trnNumber || props?.trnNumber}
            </span>
          )}
        </div>
      </div>
    );
    };

    const renderInvoiceDetails = () => {
      // Compact template - single line invoice details
      if (isCompact) {
        return (
          <div className="template-invoice-container" style={{ padding: '3px 0', fontSize: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
              <span style={{ fontSize: '9px', fontWeight: '700' }}>{receiptData?.name}</span>
              <span style={{ fontSize: '9px', fontWeight: '700' }}>{receiptData?.ordType}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '8px' }}>
              <span style={{ fontWeight: '600' }}>{convertDateFormat(getOrderDateString(receiptData?.orderDate).split(' ')[0])} {convertToAMPM(getOrderDateString(receiptData?.orderDate).split(' ')[1])}</span>
              {receiptData?.cashier && <span style={{ fontWeight: '600' }}>Cashier: {receiptData?.cashier}</span>}
            </div>
            {receiptData?.customerName && (
              <div style={{ fontSize: '8px', marginTop: '1px' }}>
                <span style={{ fontWeight: '600' }}>Customer: {receiptData?.customerName?.name || receiptData?.customerName}</span>
              </div>
            )}
          </div>
        );
      }

      // Space Saver template (8) - date/time, bill no, GST, cashier
      if (isSpaceSaver) {
        return (
          <div className="template-invoice-container" style={{ padding: '4px 0', fontSize: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
              <span style={{ fontWeight: '700' }}>
                {convertDateFormat(getOrderDateString(receiptData?.orderDate).split(' ')[0])} {convertToAMPM(getOrderDateString(receiptData?.orderDate).split(' ')[1])}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
              <span style={{ fontWeight: '700' }}>Bill No:{receiptData?.name || "---"}</span>
            </div>
            {(receiptData?.trnNumber || props?.trnNumber) && (
              <div style={{ marginBottom: '2px' }}>
                <span style={{ fontWeight: '700' }}>GST:{receiptData?.trnNumber || props?.trnNumber}</span>
              </div>
            )}
            {receiptData?.cashier && (
              <div style={{ textAlign: 'right', marginBottom: '2px' }}>
                <span style={{ fontWeight: '700' }}>Cashier:{receiptData?.cashier}</span>
              </div>
            )}
          </div>
        );
      }

      // Format 4 template (9) - date/time, bill no, table/chair, GST, cashier, customer
      if (isFormat4) {
        return (
          <div className="template-invoice-container" style={{ display: 'block', padding: '0px 0', fontSize: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1px' }}>
              <span style={{ fontWeight: '700' }}>
                {convertDateFormat(getOrderDateString(receiptData?.orderDate).split(' ')[0])} {convertToAMPM(getOrderDateString(receiptData?.orderDate).split(' ')[1])}
              </span>
            </div>
            {receiptData?.customerName && (
              <div style={{ marginBottom: '1px' }}>
                <span style={{ fontWeight: '700' }}>Customer:{receiptData?.customerName?.name || receiptData?.customerName}</span>
              </div>
            )}
            <div style={{ marginBottom: '1px' }}>
              <span style={{ fontWeight: '700' }}>Bill No:{receiptData?.name || "---"}</span>
            </div>
            {receiptData?.cashier && (
              <div style={{ marginBottom: '1px' }}>
                <span style={{ fontWeight: '700' }}>Cashier:{receiptData?.cashier}</span>
              </div>
            )}
            {(receiptData?.trnNumber || props?.trnNumber) && (
              <div style={{ marginBottom: '1px' }}>
                <span style={{ fontWeight: '700', color: '#000' }}>GST:{receiptData?.trnNumber || props?.trnNumber}</span>
              </div>
            )}
            {receiptData?.tables && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1px' }}>
                <span style={{ fontWeight: '700' }}>{receiptData?.tables}</span>
              </div>
            )}
          </div>
        );
      }

      // Token Style template (11) - date centered, order type, token on one line, quick bill, pay mode
      if (isTokenStyle) {
        const ordTypeLabel = receiptData?.ordType === 'DINEIN' ? 'Dine In' : receiptData?.ordType === 'TAKEAWAY' ? 'Take Away' : receiptData?.ordType === 'DELIVERY' ? 'Delivery' : receiptData?.ordType || '';
        return (
          <div className="template-invoice-container" style={{ padding: '4px 0', fontSize: '11px', textAlign: 'center' }}>
            <div style={{ marginBottom: '3px' }}>
              <span style={{ fontWeight: '400' }}>
                {convertDateFormat(getOrderDateString(receiptData?.orderDate).split(' ')[0])} {convertToAMPM(getOrderDateString(receiptData?.orderDate).split(' ')[1])}
              </span>
            </div>
            <div style={{ marginBottom: '4px', fontSize: '12px' }}>
              <span style={{ fontWeight: '700' }}>{ordTypeLabel}</span>
            </div>
            {/* Bill Number */}
            <div style={{ marginBottom: '4px', fontSize: '12px' }}>
              <span style={{ fontWeight: '700' }}>Bill No: {receiptData?.name || "---"}</span>
            </div>
            {/* Token Number - on one line bold */}
            <div style={{ margin: '4px 0', textAlign: 'center' }}>
              <span style={{ fontSize: '13px', fontWeight: '700' }}>Your Token No.: {receiptData?.kotInfo?.join(",") || "--"}</span>
            </div>
            <hr className="template-dashed-line" />
            {/* Quick Bill & User */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px', fontSize: '10px' }}>
              {receiptData?.isQuickBill && (
                <span style={{ fontWeight: '700' }}>Quick Bill</span>
              )}
              <span style={{ marginLeft: receiptData?.isQuickBill ? '0' : 'auto' }}>User : {receiptData?.cashier || '---'}</span>
            </div>
            {receiptData?.paymentInfo?.[0]?.type && (
              <div style={{ textAlign: 'left', marginTop: '2px', fontSize: '10px' }}>
                <span style={{ fontWeight: '600' }}>Pay Mode: {receiptData?.paymentInfo[0]?.type?.charAt(0).toUpperCase() + receiptData?.paymentInfo[0]?.type?.slice(1)}</span>
              </div>
            )}
          </div>
        );
      }

      // Items Image template (10) - date/time, bill no, customer + phone, cashier, GST, table, KOT (bold)
      if (isItemsImage) {
        return (
          <div className="template-invoice-container" style={{ display: 'block', padding: '0px 0', fontSize: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1px' }}>
              <span style={{ fontWeight: '700' }}>
                {convertDateFormat(getOrderDateString(receiptData?.orderDate).split(' ')[0])} {convertToAMPM(getOrderDateString(receiptData?.orderDate).split(' ')[1])}
              </span>
            </div>
            {receiptData?.customerName && (
              <div style={{ marginBottom: '1px' }}>
                <span style={{ fontWeight: '700' }}>Customer: {receiptData?.customerName?.name || receiptData?.customerName}</span>
              </div>
            )}
            {(receiptData?.customerName?.mobileNo || receiptData?.mobileNo) && (
              <div style={{ marginBottom: '1px' }}>
                <span style={{ fontWeight: '700' }}>Phone: {receiptData?.customerName?.mobileNo || receiptData?.mobileNo}</span>
              </div>
            )}
            <div style={{ marginBottom: '1px' }}>
              <span style={{ fontWeight: '700' }}>Bill No: {receiptData?.name || "---"}</span>
            </div>
            {receiptData?.cashier && (
              <div style={{ marginBottom: '1px' }}>
                <span style={{ fontWeight: '700' }}>Cashier: {receiptData?.cashier}</span>
              </div>
            )}
            {(receiptData?.trnNumber || props?.trnNumber) && (
              <div style={{ marginBottom: '1px' }}>
                <span style={{ fontWeight: '700', color: '#000' }}>GST: {receiptData?.trnNumber || props?.trnNumber}</span>
              </div>
            )}
            {receiptData?.tables && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1px' }}>
                <span style={{ fontWeight: '700' }}>{receiptData?.tables}</span>
              </div>
            )}
            <div style={{ marginBottom: '1px' }}>
              <span style={{ fontWeight: '800', fontSize: '11px' }}>KOT: {receiptData?.kotInfo?.join(",") || "-NIL-"}</span>
            </div>
          </div>
        );
      }
      
      // Arabic Premium template - RTL elegant invoice details
      if (isArabicPremium) {
        return (
          <div className="template-invoice-container" style={{ 
            padding: '6px 0',
            background: '#f9f9f9',
            margin: '4px 0',
            borderRadius: '2px',
            direction: 'rtl'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <div>
                <span style={{ fontSize: '11px', fontWeight: '700' }}>فاتورة #{receiptData?.name}</span>
              </div>
              <div style={{ textAlign: 'left' }}>
                <span style={{ 
                  fontSize: '9px', 
                  background: '#333', 
                  color: '#fff', 
                  padding: '2px 6px', 
                  borderRadius: '2px' 
                }}>
                  {receiptData?.ordType === 'DINE_IN' ? 'تناول الطعام' : receiptData?.ordType === 'TAKE_AWAY' ? 'سفري' : receiptData?.ordType === 'DELIVERY' ? 'توصيل' : receiptData?.ordType}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#000' }}>
              <span>{convertDateFormat(getOrderDateString(receiptData?.orderDate).split(' ')[0])} • {convertToAMPM(getOrderDateString(receiptData?.orderDate).split(' ')[1])}</span>
              {receiptData?.cashier && <span>الكاشير: {receiptData?.cashier}</span>}
            </div>
            {receiptData?.customerName && (
              <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px dashed #ddd', fontSize: '9px' }}>
                <span style={{ fontWeight: '600' }}>الضيف: </span>
                <span>{receiptData?.customerName?.name || receiptData?.customerName}</span>
              </div>
            )}
            {receiptData?.tables?.length !== 0 && (
              <div style={{ fontSize: '9px', marginTop: '2px' }}>
                <span style={{ fontWeight: '600' }}>الطاولة: </span>
                <span>{receiptData?.tables}</span>
              </div>
            )}
          </div>
        );
      }
      
      // Premium template - elegant invoice details
      if (isPremium) {
        return (
          <div className="template-invoice-container" style={{ 
            padding: '6px 0',
            background: '#f9f9f9',
            margin: '4px 0',
            borderRadius: '2px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <div>
                <span style={{ fontSize: '11px', fontWeight: '700' }}>Invoice #{receiptData?.name}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ 
                  fontSize: '9px', 
                  background: '#333', 
                  color: '#fff', 
                  padding: '2px 6px', 
                  borderRadius: '2px' 
                }}>
                  {receiptData?.ordType}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#000' }}>
              <span>{convertDateFormat(getOrderDateString(receiptData?.orderDate).split(' ')[0])} • {convertToAMPM(getOrderDateString(receiptData?.orderDate).split(' ')[1])}</span>
              {receiptData?.cashier && <span>Served by: {receiptData?.cashier}</span>}
            </div>
            {receiptData?.customerName && (
              <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px dashed #ddd', fontSize: '9px' }}>
                <span style={{ fontWeight: '600' }}>Guest: </span>
                <span>{receiptData?.customerName?.name || receiptData?.customerName}</span>
              </div>
            )}
            {receiptData?.tables?.length !== 0 && (
              <div style={{ fontSize: '9px', marginTop: '2px' }}>
                <span style={{ fontWeight: '600' }}>Table: </span>
                <span>{receiptData?.tables}</span>
              </div>
            )}
          </div>
        );
      }
      
      // Default template
      return (
      <div className="template-invoice-container">
        <div className="template-invoice-details">
          <span className="temp-bol-txt" style={templateConfig.textStyle}>BILL NO</span>:
          <span className="temp-bol-txt" style={templateConfig.textStyle}>{receiptData?.name || "---"}</span>
        </div>
        <div className="template-invoice-details temp-text-end">
          <span className="temp-bol-txt" style={templateConfig.textStyle}>&nbsp;{receiptData?.ordType}</span>
        </div>
 
        {receiptData?.customerName && (
          <div className="template-invoice-details">
            <span style={templateConfig.textStyle}>CUSTOMER :</span>
            <span style={templateConfig.textStyle}>{receiptData?.customerName?.name || receiptData?.customerName}</span>
          </div>
        )}
        <div className="template-invoice-details temp-text-end">
          <span style={templateConfig.textStyle}>
            {convertDateFormat(getOrderDateString(receiptData?.orderDate).split(' ')[0])} &nbsp;
            {convertToAMPM(getOrderDateString(receiptData?.orderDate).split(' ')[1])}
          </span>
        </div>
        {receiptData?.customerName?.mobileNo || receiptData?.mobileNo ? (
          <div className="template-invoice-details">
            <span style={templateConfig.textStyle}>MOBILE:</span>
            <span style={templateConfig.textStyle}>{receiptData?.customerName?.mobileNo || receiptData?.mobileNo}</span>
          </div>
        ) : <div></div>}
        {receiptData?.cashier ? (
          <div className="template-invoice-details temp-text-end">
            <span style={templateConfig.textStyle}>CASHIER :</span>
            <span style={templateConfig.textStyle}>{receiptData?.cashier}</span>
          </div>
        ) : <div></div>}
 
        {receiptData?.customerName && (
          <div className="template-invoice-details">
            <span style={templateConfig.textStyle}>ADDRESS:</span>
            <span style={templateConfig.textStyle}>
              {receiptData?.customerName?.city && receiptData.customerName.city + ','}
              {receiptData?.customerName?.streetName && receiptData.customerName.streetName}
              <br />
              {receiptData?.customerName?.buildingNo && receiptData.customerName.buildingNo + '-'}
              {receiptData?.customerName?.roomNo && receiptData.customerName.roomNo}
            </span>
          </div>
        )}
        {receiptData?.waiterName ? (
          <div className="template-invoice-details temp-text-end">
            <span style={templateConfig.textStyle}>WAITER&nbsp; :</span>
            <span style={templateConfig.textStyle}>{receiptData?.waiterName}</span>
          </div>
        ) : <div></div>}
        {receiptData?.tables?.length !== 0 ? (
          <div className="template-invoice-details">
            <span style={templateConfig.textStyle}>TABLE NO :</span>
            <span style={templateConfig.textStyle}>{receiptData?.tables}</span>
          </div>
        ) : <div></div>}
        <div className="template-invoice-details temp-text-end">
          <span style={templateConfig.textStyle}>KOT NO</span>:
          <span style={templateConfig.textStyle}>{receiptData?.kotInfo?.join(",") || "-NIL-"}</span>
        </div>
      </div>
    );
    };

    const renderTableHeader = () => {
      // Arabic Premium - Full Arabic table header
      if (isArabicPremium) {
        return (
          <thead>
            <tr style={{ direction: 'rtl' }}>
              <th style={{ width: '40%' }}>
                <div className="temp-table-head">
                  <span style={templateConfig.textStyle}>الصنف</span>
                </div>
              </th>
              <th style={{ width: '15%' }}>
                <div className="temp-table-head">
                  <span style={templateConfig.textStyle}>الكمية</span>
                </div>
              </th>
              <th style={{ width: '20%' }}>
                <div className="temp-table-head">
                  <span style={templateConfig.textStyle}>السعر</span>
                </div>
              </th>
              <th style={{ width: '25%' }}>
                <div className="temp-table-head">
                  <span style={templateConfig.textStyle}>المبلغ</span>
                </div>
              </th>
            </tr>
          </thead>
        );
      }
      
      // Space Saver & Format 4 & Items Image - ITEM, BASE PRICE, QTY, T.VALUE
      if (isSpaceSaver || isFormat4 || isItemsImage) {
        return (
          <thead>
            <tr>
              <th style={{ width: '35%' }}>
                <div className="temp-table-head">
                  <span style={templateConfig.textStyle}>ITEM</span>
                </div>
              </th>
              <th style={{ width: '25%' }}>
                <div className="temp-table-head">
                  <span style={templateConfig.textStyle}>BASE PRICE</span>
                </div>
              </th>
              <th style={{ width: '15%' }}>
                <div className="temp-table-head">
                  <span style={templateConfig.textStyle}>QTY</span>
                </div>
              </th>
              <th style={{ width: '25%' }}>
                <div className="temp-table-head">
                  <span style={templateConfig.textStyle}>T.VALUE</span>
                </div>
              </th>
            </tr>
          </thead>
        );
      }

      // Token Style template (11) - Item, Qty, Rate, Total
      if (isTokenStyle) {
        return (
          <thead>
            <tr style={{ borderBottom: '1px solid #333' }}>
              <th style={{ width: '40%', textAlign: 'left' }}>
                <div className="temp-table-head">
                  <span style={{ ...templateConfig.textStyle, fontWeight: '700' }}>Item</span>
                </div>
              </th>
              <th style={{ width: '15%', textAlign: 'right' }}>
                <div className="temp-table-head">
                  <span style={{ ...templateConfig.textStyle, fontWeight: '700' }}>Qty</span>
                </div>
              </th>
              <th style={{ width: '20%', textAlign: 'center' }}>
                <div className="temp-table-head">
                  <span style={{ ...templateConfig.textStyle, fontWeight: '700' }}>Rate</span>
                </div>
              </th>
              <th style={{ width: '25%', textAlign: 'center' }}>
                <div className="temp-table-head">
                  <span style={{ ...templateConfig.textStyle, fontWeight: '700' }}>Total</span>
                </div>
              </th>
            </tr>
          </thead>
        );
      }

      return (
      <thead>
        <tr>
          <th style={{ width: '40%' }}>
            <div className="temp-table-head">
              <span style={templateConfig.textStyle}>Item</span>
              {tempalteType === 3 && <span style={templateConfig.textStyle}>غرض</span>}
            </div>
          </th>
          <th style={{ width: '15%' }}>
            <div className="temp-table-head">
              <span style={templateConfig.textStyle}>Qty</span>
              {tempalteType === 3 && <span style={templateConfig.textStyle}>الكمية</span>}
            </div>
          </th>
          <th style={{ width: '20%' }}>
            <div className="temp-table-head">
              <span style={templateConfig.textStyle}>Price</span>
              {tempalteType === 3 && <span style={templateConfig.textStyle}>سعر</span>}
            </div>
          </th>
          <th style={{ width: '25%' }}>
            <div className="temp-table-head">
              <span style={templateConfig.textStyle}>Amount</span>
              {tempalteType === 3 && <span style={templateConfig.textStyle}>كمية</span>}
            </div>
          </th>
        </tr>
      </thead>
    );
    };
 
    const renderTableFooter = () => {
      // Token Style template (11) - Total, CGST, SGST, Grand Total, Rounded Amount
      if (isTokenStyle) {
        const totalQty = receiptData?.orderInfo?.reduce((sum, item) => sum + (item.qty || 0), 0);
        const roundOffVal = receiptData?.roundOff || 0;
        
        return (
          <tfoot>
            <tr style={{ borderTop: '1px solid #333' }}>
              <td style={{ textAlign: 'left', fontWeight: '700' }}>
                <span style={templateConfig.textStyle}>Total :</span>
              </td>
              <td style={{ textAlign: 'right', fontWeight: '700' }}>
                <span style={templateConfig.textStyle}>{totalQty}</span>
              </td>
              <td></td>
              <td style={{ textAlign: 'right', fontWeight: '700' }}>
                <span style={templateConfig.textStyle}>Rs {receiptData?.taxableAmt?.toFixed(decimalPosition)}</span>
              </td>
            </tr>
            {receiptData?.taxDetails?.length > 0 &&
              receiptData?.taxDetails?.map((item, i) => (
                <tr key={i}>
                  <td colSpan={3} style={{ textAlign: 'right' }}>
                    <span style={templateConfig.textStyle}>{item?.taxName}:</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={templateConfig.textStyle}>Rs{item?.amount?.toFixed(decimalPosition)}</span>
                  </td>
                </tr>
              ))}
            <tr className="temp-net-amount">
              <td colSpan={2} style={{ textAlign: 'left', fontWeight: '700' }}>
                <span className="temp-bol-txt" style={{ ...templateConfig.textStyle, fontSize: '12px' }}>Grand Total :</span>
              </td>
              <td colSpan={2} style={{ textAlign: 'right', fontWeight: '700' }}>
                <span className="temp-bol-txt" style={{ ...templateConfig.textStyle, fontSize: '12px' }}>Rs {receiptData?.amtTotal?.toFixed(decimalPosition)}</span>
              </td>
            </tr>
            {roundOffVal !== 0 && (
              <tr>
                <td colSpan={2} style={{ textAlign: 'left' }}>
                  <span style={templateConfig.textStyle}>Rounded Amount :</span>
                </td>
                <td colSpan={2} style={{ textAlign: 'right' }}>
                  <span style={templateConfig.textStyle}>{roundOffVal > 0 ? '+' : ''}{roundOffVal?.toFixed(2)}</span>
                </td>
              </tr>
            )}
          </tfoot>
        );
      }

      // Space Saver template (8) - Sub Total, tax lines if available, Grand Total
      if (isSpaceSaver) {
        return (
          <tfoot>
            <tr>
              <td colSpan={3} style={{ textAlign: 'right', fontWeight: '700' }}>
                <span style={templateConfig.textStyle}>Sub Total:</span>
              </td>
              <td style={{ textAlign: 'right', fontWeight: '700' }}>
                <span style={templateConfig.textStyle}>{receiptData?.taxableAmt?.toFixed(decimalPosition)}</span>
              </td>
            </tr>
            {receiptData?.taxDetails?.length > 0 &&
              receiptData?.taxDetails?.map((item, i) => (
                <tr key={i}>
                  <td colSpan={3} style={{ textAlign: 'right' }}>
                    <span style={templateConfig.textStyle}>{item?.taxName}:</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={templateConfig.textStyle}>{item?.amount?.toFixed(decimalPosition)}</span>
                  </td>
                </tr>
              ))}
            <tr className="temp-net-amount">
              <td colSpan={3} style={{ textAlign: 'right', fontWeight: '700' }}>
                <span className="temp-bol-txt" style={templateConfig.textStyle}>Grand Total:</span>
              </td>
              <td style={{ textAlign: 'right', fontWeight: '700' }}>
                <span className="temp-bol-txt" style={templateConfig.textStyle}>{receiptData?.amtTotal?.toFixed(decimalPosition)}</span>
              </td>
            </tr>
          </tfoot>
        );
      }

      // Format 4 / Items Image template (9/10) - Cart Total, Service Charge, Sub Total, tax lines, Grand Total
      if (isFormat4 || isItemsImage) {
        return (
          <tfoot>
            <tr>
              <td colSpan={3} style={{ textAlign: 'right', fontWeight: '700' }}>
                <span style={templateConfig.textStyle}>Cart Total:</span>
              </td>
              <td style={{ textAlign: 'right', fontWeight: '700' }}>
                <span style={templateConfig.textStyle}>{receiptData?.taxableAmt?.toFixed(decimalPosition)}</span>
              </td>
            </tr>
            {receiptData?.serviceCharge > 0 && (
              <tr>
                <td colSpan={3} style={{ textAlign: 'right' }}>
                  <span style={templateConfig.textStyle}>Service Charge @{receiptData?.serviceChargePercentage || ''}%:</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span style={templateConfig.textStyle}>{receiptData?.serviceCharge?.toFixed(decimalPosition)}</span>
                </td>
              </tr>
            )}
            <tr>
              <td colSpan={3} style={{ textAlign: 'right', fontWeight: '700' }}>
                <span style={templateConfig.textStyle}>Sub Total:</span>
              </td>
              <td style={{ textAlign: 'right', fontWeight: '700' }}>
                <span style={templateConfig.textStyle}>{((receiptData?.taxableAmt || 0) + (receiptData?.serviceCharge || 0))?.toFixed(decimalPosition)}</span>
              </td>
            </tr>
            {receiptData?.taxDetails?.length > 0 &&
              receiptData?.taxDetails?.map((item, i) => (
                <tr key={i}>
                  <td colSpan={3} style={{ textAlign: 'right' }}>
                    <span style={templateConfig.textStyle}>{item?.taxName}:</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={templateConfig.textStyle}>{item?.amount?.toFixed(decimalPosition)}</span>
                  </td>
                </tr>
              ))}
            {receiptData?.deliveryCharge > 0 && (
              <tr>
                <td colSpan={3} style={{ textAlign: 'right' }}>
                  <span style={templateConfig.textStyle}>Delivery Charge:</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span style={templateConfig.textStyle}>{receiptData?.deliveryCharge?.toFixed(decimalPosition)}</span>
                </td>
              </tr>
            )}
            {receiptData?.pkgCharge > 0 && (
              <tr>
                <td colSpan={3} style={{ textAlign: 'right' }}>
                  <span style={templateConfig.textStyle}>Package Charge:</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span style={templateConfig.textStyle}>{receiptData?.pkgCharge?.toFixed(decimalPosition)}</span>
                </td>
              </tr>
            )}
            {receiptData?.discountAmt > 0 && (
              <tr>
                <td colSpan={3} style={{ textAlign: 'right' }}>
                  <span style={templateConfig.textStyle}>Discount:</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span style={templateConfig.textStyle}>{receiptData?.discountAmt?.toFixed(decimalPosition)}</span>
                </td>
              </tr>
            )}
            <tr className="temp-net-amount">
              <td colSpan={3} style={{ textAlign: 'right', fontWeight: '700' }}>
                <span className="temp-bol-txt" style={templateConfig.textStyle}>Grand Total:</span>
              </td>
              <td style={{ textAlign: 'right', fontWeight: '700' }}>
                <span className="temp-bol-txt" style={templateConfig.textStyle}>{receiptData?.amtTotal?.toFixed(decimalPosition)}</span>
              </td>
            </tr>
          </tfoot>
        );
      }

      // Arabic Premium - Full Arabic table footer (right-aligned for RTL)
      if (isArabicPremium) {
        return (
          <tfoot style={{ direction: 'rtl', textAlign: 'right' }}>
            <tr>
              <td colSpan={2} style={{ textAlign: 'right' }}>
                <span style={templateConfig.textStyle}>المبلغ الخاضع للضريبة</span>
              </td>
              <td colSpan={2} style={{ textAlign: 'left' }}>
                <span style={templateConfig.textStyle}>{convertToArabicNumber(Number(receiptData?.taxableAmt?.toFixed(decimalPosition)))}</span>
              </td>
            </tr>
            {receiptData?.taxDetails?.length > 0 &&
              receiptData?.taxDetails?.map((item, i) => (
                <tr key={i}>
                  <td colSpan={2} style={{ textAlign: 'right' }}>
                    <span style={templateConfig.textStyle}>{item?.taxName}</span>
                  </td>
                  <td colSpan={2} style={{ textAlign: 'left' }}>
                    <span style={templateConfig.textStyle}>{convertToArabicNumber(Number(item?.amount?.toFixed(decimalPosition)))}</span>
                  </td>
                </tr>
              ))}
            {receiptData?.deliveryCharge > 0 && (
              <tr>
                <td colSpan={2} style={{ textAlign: 'right' }}>
                  <span style={templateConfig.textStyle}>رسوم التوصيل</span>
                </td>
                <td colSpan={2} style={{ textAlign: 'left' }}>
                  <span style={templateConfig.textStyle}>{convertToArabicNumber(Number(receiptData?.deliveryCharge?.toFixed(decimalPosition)))}</span>
                </td>
              </tr>
            )}
            {receiptData?.pkgCharge > 0 && (
              <tr>
                <td colSpan={2} style={{ textAlign: 'right' }}>
                  <span style={templateConfig.textStyle}>رسوم التغليف</span>
                </td>
                <td colSpan={2} style={{ textAlign: 'left' }}>
                  <span style={templateConfig.textStyle}>{convertToArabicNumber(Number(receiptData?.pkgCharge?.toFixed(decimalPosition)))}</span>
                </td>
              </tr>
            )}
            {receiptData?.discountAmt > 0 && (
              <tr>
                <td colSpan={2} style={{ textAlign: 'right' }}>
                  <span style={templateConfig.textStyle}>الخصم</span>
                </td>
                <td colSpan={2} style={{ textAlign: 'left' }}>
                  <span style={templateConfig.textStyle}>{convertToArabicNumber(Number(receiptData?.discountAmt?.toFixed(decimalPosition)))}</span>
                </td>
              </tr>
            )}
            <tr className="temp-net-amount">
              <td colSpan={2} style={{ textAlign: 'right' }}>
                <span className="temp-bol-txt" style={templateConfig.textStyle}>المبلغ الصافي</span>
              </td>
              <td colSpan={2} style={{ textAlign: 'left' }}>
                <span style={templateConfig.textStyle}>{convertToArabicNumber(Number(receiptData?.amtTotal?.toFixed(decimalPosition)))}</span>
              </td>
            </tr>
            <tr className="temp-last-raw">
              <td colSpan={4} style={{ textAlign: 'right' }}>
                <div>
                  <span style={templateConfig.textStyle}>
                    {numToArabicWords(Number(receiptData?.amtTotal?.toFixed(decimalPosition)))}
                  </span>
                </div>
              </td>
            </tr>
          </tfoot>
        );
      }
      
      return (
      <tfoot>
        <tr>
          <td></td>
          <td colSpan={2} className="table-item">
            <span style={templateConfig.textStyle}>Taxable Amount</span>
            {tempalteType === 3 && <span style={templateConfig.textStyle}>المبلغ الخاضع للضريبة</span>}
          </td>
          <td colSpan={2} className="temp-text-end">
            {tempalteType === 3 && (
              <span style={templateConfig.textStyle}>
                {convertToArabicNumber(Number(receiptData?.taxableAmt?.toFixed(decimalPosition)))} &nbsp; &nbsp;&nbsp;
              </span>
            )}
            <span style={templateConfig.textStyle}>{receiptData?.taxableAmt?.toFixed(decimalPosition)}</span>
          </td>
        </tr>
        {receiptData?.taxDetails?.length > 0 &&
          receiptData?.taxDetails?.map((item, i) => (
            <tr key={i}>
              <td></td>
              <td colSpan={2}>
                <span style={templateConfig.textStyle}>{item?.taxName}</span>
              </td>
              <td className="temp-text-end">
                {tempalteType === 3 && (
                  <span style={templateConfig.textStyle}>
                    {convertToArabicNumber(Number(item?.amount?.toFixed(decimalPosition)))} &nbsp; &nbsp;&nbsp;
                  </span>
                )}
                <span style={templateConfig.textStyle}>{item?.amount?.toFixed(decimalPosition)}</span>
              </td>
            </tr>
          ))}
        {receiptData?.deliveryCharge > 0 && (
          <tr>
            <td></td>
            <td colSpan={2}>
              <span style={templateConfig.textStyle}>Delivery Charge</span>
            </td>
            <td className="temp-text-end">
              {tempalteType === 3 && (
                <span style={templateConfig.textStyle}>
                  {convertToArabicNumber(Number(receiptData?.deliveryCharge?.toFixed(decimalPosition)))} &nbsp; &nbsp;&nbsp;
                </span>
              )}
              <span style={templateConfig.textStyle}>{receiptData?.deliveryCharge?.toFixed(decimalPosition)}</span>
            </td>
          </tr>
        )}
        {receiptData?.pkgCharge > 0 && (
          <tr>
            <td></td>
            <td colSpan={2}>
              <span style={templateConfig.textStyle}>Package Charge</span>
            </td>
            <td className="temp-text-end">
              {tempalteType === 3 && (
                <span style={templateConfig.textStyle}>
                  {convertToArabicNumber(Number(receiptData?.pkgCharge?.toFixed(decimalPosition)))} &nbsp; &nbsp;&nbsp;
                </span>
              )}
              <span style={templateConfig.textStyle}>{receiptData?.pkgCharge?.toFixed(decimalPosition)}</span>
            </td>
          </tr>
        )}
        {receiptData?.discountAmt > 0 && (
          <tr>
            <td></td>
            <td colSpan={2}>
              <span style={templateConfig.textStyle}>Discount</span>
            </td>
            <td className="temp-text-end">
              {tempalteType === 3 && (
                <span style={templateConfig.textStyle}>
                  {convertToArabicNumber(Number(receiptData?.discountAmt?.toFixed(decimalPosition)))} &nbsp; &nbsp;&nbsp;
                </span>
              )}
              <span style={templateConfig.textStyle}>{receiptData?.discountAmt?.toFixed(decimalPosition)}</span>
            </td>
          </tr>
        )}
        {/* Free Delivery Promo Info */}
        {receiptData?.freeDeliveryApplied && (
          <tr>
            <td></td>
            <td colSpan={2}>
              <span style={{ ...templateConfig.textStyle, color: '#e65100' }}>
                🚚 Free Delivery {receiptData?.freeDeliveryKm > 0 ? `(${receiptData?.freeDeliveryKm} KM)` : ''}
              </span>
            </td>
            <td className="temp-text-end">
              <span style={{ ...templateConfig.textStyle, color: '#4CAF50' }}>
                -{receiptData?.freeDeliverySaved?.toFixed(decimalPosition)}
              </span>
            </td>
          </tr>
        )}
        {/* Promo Code Applied */}
        {receiptData?.promoCode && !receiptData?.freeDeliveryApplied && (
          <tr>
            <td></td>
            <td colSpan={3}>
              <span style={{ ...templateConfig.textStyle, fontSize: '10px', color: '#666' }}>
                🎫 Promo: {receiptData?.promoCode}
              </span>
            </td>
          </tr>
        )}
        <tr className="temp-net-amount">
          <td></td>
          <td colSpan={2} className="table-item">
            <span className="temp-bol-txt" style={templateConfig.textStyle}>Net Amount</span>
            {tempalteType === 3 && <span className="temp-bol-txt" style={templateConfig.textStyle}>كمية الشبكة</span>}
          </td>
          <td colSpan={2} className="temp-text-end temp-bol-txt">
            {tempalteType === 3 && (
              <span style={templateConfig.textStyle}>
                {convertToArabicNumber(Number(receiptData?.amtTotal?.toFixed(decimalPosition)))} &nbsp;&nbsp;&nbsp;
              </span>
            )}
            <span style={templateConfig.textStyle}>{receiptData?.amtTotal?.toFixed(decimalPosition)}</span>
          </td>
        </tr>
        <tr className="temp-last-raw">
          <td colSpan={4} className="temp-text-end temp-bol-txt">
            <div>
              <span style={templateConfig.textStyle}>
                {numToWords(Number(receiptData?.amtTotal?.toFixed(decimalPosition)))}
              </span>
            </div>
          </td>
        </tr>
      </tfoot>
    );
    };

    const renderPaymentDetails = () => {
      // Space Saver & Token Style template (8, 11) - no separate payment details section
      if (isSpaceSaver || isTokenStyle) {
        return null;
      }

      // Format 4 / Items Image template (9/10) - Tender, Change (only if > 0), Payment Mode
      if (isFormat4 || isItemsImage) {
        const totalPaid = (receiptData?.paymentInfo || []).reduce((sum, item) => sum + (item.amount || 0), 0);
        const change = totalPaid - (receiptData?.amtTotal || 0);
        const paymentModes = (receiptData?.paymentInfo || []).map(item => item.type?.charAt(0).toUpperCase() + item.type?.slice(1)).join(', ');
        return (
          <div className="template-payment-details" style={{ padding: '4px 0', fontSize: '10px' }}>
            <hr className="template-dashed-line" />
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: '700', padding: '1px 0', width: '50%' }}>Tender</td>
                  <td style={{ fontWeight: '700', padding: '1px 0', textAlign: 'right' }}>{totalPaid?.toFixed(decimalPosition)}</td>
                </tr>
                {change > 0 && (
                  <tr>
                    <td style={{ fontWeight: '700', padding: '1px 0' }}>Change</td>
                    <td style={{ fontWeight: '700', padding: '1px 0', textAlign: 'right' }}>{change?.toFixed(decimalPosition)}</td>
                  </tr>
                )}
                <tr>
                  <td style={{ fontWeight: '700', padding: '1px 0' }}>Payment Mode</td>
                  <td style={{ fontWeight: '700', padding: '1px 0', textAlign: 'right' }}>{paymentModes || 'Cash'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      }

      // Arabic Premium - Full Arabic payment details
      if (isArabicPremium) {
        return (
          <div className="template-payment-details" style={{ direction: 'rtl' }}>
            <div className="template-pax-count">
              <div>
                <span className="temp-bol-txt" style={templateConfig.textStyle}>عدد الأصناف :</span>
                <span className="temp-bol-txt" style={templateConfig.textStyle}>{convertToArabicNumber(receiptData?.orderInfo?.length)}</span>
              </div>
            </div>
            <div className="template-payment-via">
              <div className="temp-text-end justify-space-between">
                <span className="temp-bol-txt" style={{ ...templateConfig.textStyle, fontWeight: '700', fontSize: '12px' }}>الدفع</span>
              </div>
              {(receiptData?.paymentInfo || []).map((item, i) => (
                <div key={i} className="temp-text-end justify-space-between">
                  <span className="temp-bol-txt" style={{ ...templateConfig.textStyle, fontWeight: '700', fontSize: '11px' }}>
                    {item.type === 'cash' ? 'نقدي' : item.type === 'card' ? 'بطاقة' : item.type?.toUpperCase()}
                  </span>
                  <span className="temp-bol-txt" style={{ marginRight: "12px", ...templateConfig.textStyle, fontWeight: '700', fontSize: '11px' }}>
                    {convertToArabicNumber(Number(item.amount?.toFixed(decimalPosition)))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      }
      
      return (
      <div className="template-payment-details">
        <div className="template-pax-count">
          <div>
            <span className="temp-bol-txt" style={templateConfig.textStyle}>PAX :</span>
            <span className="temp-bol-txt" style={templateConfig.textStyle}>{receiptData?.orderInfo?.length}</span>
          </div>
          {tempalteType === 3 && (
            <div>
              <span className="temp-bol-txt" style={templateConfig.textStyle}>باكس :</span>
              <span className="temp-bol-txt" style={templateConfig.textStyle}>
                {convertToArabicNumber(receiptData?.orderInfo?.length)}
              </span>
            </div>
          )}
        </div>
        <div className="template-payment-via">
          <div className="temp-text-end justify-space-between">
            <span className="temp-bol-txt" style={{ ...templateConfig.textStyle, fontWeight: '700', fontSize: '12px' }}>PAYMENT</span>
            {tempalteType === 3 && (
              <span style={{ marginLeft: "12px", fontWeight: '700', fontSize: '12px' }} className="temp-bol-txt" >قسط</span>
            )}
          </div>
          {(receiptData?.paymentInfo || []).map((item, i) => (
            <div key={i} className="temp-text-end justify-space-between">
              <span className="temp-bol-txt" style={{ ...templateConfig.textStyle, fontWeight: '700', fontSize: '11px' }}>{item.type?.toUpperCase()}</span>
              {tempalteType === 3 && (
                <span className="temp-bol-txt" style={{ ...templateConfig.textStyle, fontWeight: '700', fontSize: '11px' }}>
                  &nbsp; &nbsp;{convertToArabicNumber(Number(item.amount?.toFixed(decimalPosition)))} &nbsp;
                </span>
              )}
              <span className="temp-bol-txt" style={{ marginLeft: "12px", ...templateConfig.textStyle, fontWeight: '700', fontSize: '11px' }}>
                {item.amount?.toFixed(decimalPosition)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
    };
 
    const renderFooter = () => {
      // Space Saver template (8) - minimal footer with T&C if available
      if (isSpaceSaver) {
        return (
          <div className="pos-receipt-template-footer">
            <div className="template-footer-content">
              <hr className="template-dashed-line" />
              {resolvedTermsAndConditions && (
                <div style={{ padding: '4px 0', fontSize: '9px', textAlign: 'center', whiteSpace: 'pre-wrap' }}>
                  {resolvedTermsAndConditions}
                </div>
              )}
              <hr className="template-dashed-line" />
            </div>
          </div>
        );
      }

      // Format 4 / Items Image template (9/10) - Terms & Conditions (no duplicate GST breakup)
      if (isFormat4 || isItemsImage) {
        return (
          <div className="pos-receipt-template-footer">
            <div className="template-footer-content">
              <hr className="template-dashed-line" />
              {resolvedTermsAndConditions && (
                <div style={{ padding: '4px 0', fontSize: '9px', textAlign: 'center', whiteSpace: 'pre-wrap' }}>
                  {resolvedTermsAndConditions}
                </div>
              )}
              <hr className="template-dashed-line" />
            </div>
          </div>
        );
      }

      // Token Style template (11) - Customer, Paid Amount, Return Amount with dashed lines
      if (isTokenStyle) {
        const totalPaid = (receiptData?.paymentInfo || []).reduce((sum, item) => {
          const amt = Number(item.amount || item.paidAmt || 0);
          return amt > 0 ? sum + amt : sum;
        }, 0);
        const returnAmt = totalPaid - (receiptData?.amtTotal || 0);
        const custName = Array.isArray(receiptData?.customerName) ? receiptData?.customerName?.[0]?.name : (receiptData?.customerName?.name || receiptData?.customerName || '');
        const custPhone = Array.isArray(receiptData?.customerName) ? receiptData?.customerName?.[0]?.mobileNo : (receiptData?.customerName?.mobileNo || receiptData?.mobileNo || '');

        return (
          <div className="pos-receipt-template-footer">
            <div className="template-footer-content" style={{ fontSize: '11px' }}>
              {/* Customer Info */}
              {custName && (
                <>
                  <hr className="template-dashed-line" />
                  <div style={{ padding: '3px 0', fontWeight: '400' }}>
                    Customer: {custName}{custPhone ? ` (${custPhone})` : ''}
                  </div>
                </>
              )}
              {/* Paid Amount */}
              <hr className="template-dashed-line" />
              <div style={{ padding: '3px 0', fontWeight: '700' }}>
                Paid Amount: Rs {totalPaid?.toFixed(decimalPosition)}
              </div>
              {/* Return Amount */}
              {returnAmt > 0 && (
                <div style={{ padding: '3px 0', fontWeight: '700' }}>
                  Return Amount: Rs {returnAmt?.toFixed(decimalPosition)}
                </div>
              )}
              {resolvedTermsAndConditions && (
                <>
                  <hr className="template-dashed-line" />
                  <div style={{ padding: '4px 0', fontSize: '9px', textAlign: 'center', whiteSpace: 'pre-wrap' }}>
                    {resolvedTermsAndConditions}
                  </div>
                </>
              )}
              <hr className="template-dashed-line" />
            </div>
          </div>
        );
      }

      // Arabic Premium - Full Arabic footer
      if (isArabicPremium) {
        return (
          <div className="pos-receipt-template-footer" style={{ direction: 'rtl' }}>
            <div className="template-footer-content">
              {(printerSettings?.enableQrCode || printerSettings?.enableBarcode) && (
                <div className="template-ft-qr-bar-code-container justify-center" style={{ gap: '1em', marginTop: '1em', flexWrap: "wrap" }}>
                  {printerSettings?.enableQrCode && (
                    <div className="template-ft-qr">
                      <QRCode size={80} value={domain ? domain : ''} viewBox={`0 0 256 256`} />
                    </div>
                  )}
                  {printerSettings?.enableBarcode && (
                    <div className="template-ft-bar">
                      <Barcode format="CODE128" value={receiptData?.name || "###"} width={1.5} fontSize={12} height={60} margin={0} />
                    </div>
                  )}
                </div>
              )}
              {resolvedTermsAndConditions && (
                <div style={{ padding: '4px 0', fontSize: '9px', textAlign: 'center', direction: 'ltr', whiteSpace: 'pre-wrap' }}>
                  {resolvedTermsAndConditions}
                </div>
              )}
              <hr className="template-dotted-line" />
              <div className="temp-two-column">
                <span className="temp-bol-txt fr-greeting" style={templateConfig.textStyle}>شكرًا لزيارتكم</span>
              </div>
              <hr className="template-dashed-line" />
            </div>
          </div>
        );
      }
      
      return (
      <div className="pos-receipt-template-footer">
        <div className="template-footer-content">
          {/* Only show QR/Barcode section if at least one is enabled in Device Settings */}
          {(printerSettings?.enableQrCode || printerSettings?.enableBarcode) && (
          <div
            className="template-ft-qr-bar-code-container justify-center"
            style={{
              gap: tempalteType === 4 ? '0.5em' : '1em',
              marginTop: tempalteType === 4 ? '0.5em' : '1em',
              flexWrap: "wrap"
            }}
          >
            {/* QR Code - only show if enabled in Device Settings */}
            {printerSettings?.enableQrCode && (
            <div className="template-ft-qr">
              <QRCode
                size={80}
                value={domain ? domain : ''}
                viewBox={`0 0 256 256`}
              />
            </div>
            )}
            {/* Barcode - only show if enabled in Device Settings */}
            {printerSettings?.enableBarcode && (
            <div className="template-ft-bar">
              <Barcode
                format="CODE128"
                value={receiptData?.name || "###"}
                  width={1.5}
                  fontSize={12}
                  height={60}
                margin={0}
              />
            </div>
            )}
          </div>
          )}
          {resolvedTermsAndConditions && (
            <div style={{ padding: '4px 0', fontSize: '9px', textAlign: 'center', whiteSpace: 'pre-wrap' }}>
              {resolvedTermsAndConditions}
            </div>
          )}
          <hr className="template-dotted-line" />
          <div className="temp-two-column">
            <span className="temp-bol-txt fr-greeting" style={templateConfig.textStyle}>THANK YOU</span>
            {(() => {
              console.log('🧾 Arabic greeting check - templateType:', tempalteType, 'should render Arabic:', tempalteType === 3);
              return tempalteType === 3 && (
                <span className="temp-bol-txt fr-greeting" style={templateConfig.textStyle}>شكرًا لك</span>
              );
            })()}
          </div>
          <hr className="template-dashed-line" />
        </div>
      </div>
    );
    };
 
    const renderToken = () => {
      // Template 11 (Token Style) - token already shown in invoice details, don't render again
      if (isTokenStyle) {
        return null;
      }
      
      // Template 7 (Arabic Premium) - show KOT number in Arabic
      if (isArabicPremium) {
        return (
          <>
            <div className="template-token-container" style={{ textAlign: 'center', padding: '6px 0', direction: 'rtl' }}>
              <span
                style={{
                  display: 'inline-block',
                  background: '#000',
                  color: '#fff',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '700',
                  letterSpacing: '0.3px'
                }}
              >
                رقم: {receiptData?.kotInfo?.join(",") || "-NIL-"}
              </span>
            </div>
            <hr className="template-dashed-line" />
          </>
        );
      }
      
      // Template 6 (Premium) - show KOT number in elegant style
      if (isPremium) {
        return (
          <>
            <div className="template-token-container" style={{ textAlign: 'center', padding: '6px 0' }}>
              <span
                style={{
                  display: 'inline-block',
                  background: '#000',
                  color: '#fff',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '700',
                  letterSpacing: '0.3px'
                }}
              >
                NO: {receiptData?.kotInfo?.join(",") || "-NIL-"}
              </span>
            </div>
            <hr className="template-dashed-line" />
          </>
        );
      }
      
      return (
      (tempalteType === 2 || tempalteType === 4) && (
        <>
          <div className="template-token-container">
            <div style={tempalteType === 4 ? { padding: '4px 0' } : {}}>
              <span className="temp-token-name" style={templateConfig.textStyle}>YOUR TOKEN NO</span>
              <span
                className="temp-token-no"
                style={tempalteType === 4 ? { fontSize: '1.5em' } : {}}
              >
                {receiptData?.kotInfo?.join(",") || "-NIL-"}
              </span>
            </div>
          </div>
          <hr className="template-dashed-line" />
        </>
      )
    );
    };
 
    const renderPage = (pageIndex) => {
      const startIndex = pageIndex * itemsPerPage;
      const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
      const isFirstPage = pageIndex === 0;
      const isLastPage = pageIndex === totalPages - 1;
 
      return (
          <div
      key={pageIndex}
      className={`pos-receipt-template-container ${tempalteType === 4 ? 'template-three-continer' : ''} ${templateConfig.containerClass}`}
      style={templateConfig.containerStyle}
      dir={isArabicPremium ? 'rtl' : 'ltr'}
    >
      {isFirstPage && renderHeader()}
      <div className="pos-receipt-template-body">
        {isFirstPage && (
          <>
            <hr className="template-dashed-line" />
            {renderInvoiceDetails()}
            <hr className="template-dashed-line" />
          </>
        )}
 
        <div className="template-table-container">
          <table style={templateConfig.tableStyle}>
            {/* Only render header for the first page OR if it's a single page */}
            {(isFirstPage || totalPages === 1) && renderTableHeader()}
           
            <tbody>
              {(receiptData?.orderInfo || []).slice(startIndex, endIndex).map((res, i) => (
                isTokenStyle ? (
                  <React.Fragment key={i}>
                    <tr>
                      <td style={{ ...templateConfig.cellStyle, width: '40%', textAlign: 'left' }}>
                        <span className="temp-bol-txt" style={{ ...templateConfig.textStyle, fontWeight: '600' }}>
                          {res?.productName}
                        </span>
                      </td>
                      <td style={{ ...templateConfig.cellStyle, width: '15%', textAlign: 'right' }}>
                        <span style={templateConfig.textStyle}>{res?.qty}</span>
                      </td>
                      <td style={{ ...templateConfig.cellStyle, width: '20%', textAlign: 'center' }}>
                        <span style={templateConfig.textStyle}>{res?.unitPrice?.toFixed(decimalPosition)}</span>
                      </td>
                      <td style={{ ...templateConfig.cellStyle, width: '25%', textAlign: 'right' }}>
                        <span style={templateConfig.textStyle}>{res?.subTotalWoTax?.toFixed(decimalPosition)}</span>
                      </td>
                    </tr>
                    {/* Combo/special offer sub-items */}
                    {res?.itemType === 3 && res?.productList?.length > 0 && res.productList.map((sub, si) => (
                      <tr key={`sub-${si}`}>
                        <td colSpan={4} style={{ ...templateConfig.cellStyle, fontSize: '9px', color: '#555', paddingTop: '0', paddingBottom: '1px' }}>
                          <span style={templateConfig.textStyle}>{sub.qty || 1} x {sub.productName || sub.name || 'Item'}</span>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ) : isItemsImage ? (
                <React.Fragment key={i}>
                  {res?.imageUrl && (
                    <tr>
                      <td colSpan={4} style={{ padding: '4px 2px 2px 2px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <img
                            src={res.imageUrl}
                            alt={res?.productName}
                            style={{
                              width: '36px',
                              height: '36px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              flexShrink: 0
                            }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <span style={{ ...templateConfig.textStyle, fontWeight: '600', fontSize: '10px' }}>{res?.productName}</span>
                        </div>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td style={{ ...templateConfig.cellStyle, width: '35%' }}>
                      <div className="table-item-center" style={{ textAlign: "start" }}>
                        {!res?.imageUrl && (
                          <span className="temp-bol-txt" style={templateConfig.textStyle}>
                            {res?.productName}
                          </span>
                        )}
                        {res?.imageUrl && (
                          <span style={{ ...templateConfig.textStyle, fontSize: '9px', color: '#555' }}>
                            {res?.productName}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ ...templateConfig.cellStyle, width: '25%', textAlign: 'right' }}>
                      <div className="table-item-center">
                        <span style={templateConfig.textStyle}>{res?.unitPrice?.toFixed(decimalPosition)}</span>
                      </div>
                    </td>
                    <td style={{ ...templateConfig.cellStyle, width: '15%', textAlign: 'center' }}>
                      <div className="table-item-center">
                        <span style={templateConfig.textStyle}>{res?.qty}</span>
                      </div>
                    </td>
                    <td style={{ ...templateConfig.cellStyle, width: '25%', textAlign: 'right' }}>
                      <div className="table-item-center">
                        <span style={templateConfig.textStyle}>{res?.subTotalWoTax?.toFixed(decimalPosition)}</span>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
                ) : (isSpaceSaver || isFormat4) ? (
                <tr key={i}>
                  <td style={{ ...templateConfig.cellStyle, width: '35%' }}>
                    <div className="table-item-center" style={{ textAlign: "start" }}>
                      <span className="temp-bol-txt" style={templateConfig.textStyle}>
                        {res?.productName}
                      </span>
                    </div>
                  </td>
                  <td style={{ ...templateConfig.cellStyle, width: '25%', textAlign: 'right' }}>
                    <div className="table-item-center">
                      <span style={templateConfig.textStyle}>{res?.unitPrice?.toFixed(decimalPosition)}</span>
                    </div>
                  </td>
                  <td style={{ ...templateConfig.cellStyle, width: '15%', textAlign: 'center' }}>
                    <div className="table-item-center">
                      <span style={templateConfig.textStyle}>{res?.qty}</span>
                    </div>
                  </td>
                  <td style={{ ...templateConfig.cellStyle, width: '25%', textAlign: 'right' }}>
                    <div className="table-item-center">
                      <span style={templateConfig.textStyle}>{res?.subTotalWoTax?.toFixed(decimalPosition)}</span>
                    </div>
                  </td>
                </tr>
                ) : (
                <tr key={i}>
                  <td style={{ ...templateConfig.cellStyle, width: '40%' }}>
                    <div className="table-item-center" style={{ textAlign: "start" }}>
                      <span className="temp-bol-txt" style={templateConfig.textStyle}>
                        {res?.productName}
                      </span>
                      {tempalteType === 3 && (
                        <span className="temp-bol-txt" style={templateConfig.textStyle}>{res?.nativeName}</span>
                      )}
                    </div>
                  </td>
                  <td style={{ ...templateConfig.cellStyle, width: '15%', textAlign: 'right' }}>
                    <div className="table-item-center">
                      <span style={templateConfig.textStyle}>{res?.qty?.toFixed(decimalPosition)}</span>
                    </div>
                  </td>
                  <td style={{ ...templateConfig.cellStyle, width: '20%', textAlign: 'center' }}>
                    <div className="table-item-center">
                      <span style={templateConfig.textStyle}>{res?.unitPrice?.toFixed(decimalPosition)}</span>
                    </div>
                  </td>
                  <td style={{ ...templateConfig.cellStyle, width: '25%', textAlign: 'right' }}>
                    <div className="table-item-center">
                      <span style={templateConfig.textStyle}>{res?.subTotalWoTax?.toFixed(decimalPosition)}</span>
                    </div>
                  </td>
                </tr>
                )
              ))}
            </tbody>
           
            {isLastPage && renderTableFooter()}
          </table>
        </div>
 
        {isLastPage && (
          <>
            <hr className="template-dashed-line" />
            {renderPaymentDetails()}
            <hr className="template-dashed-line" />
            {renderToken()}
          </>
        )}
      </div>
      {isLastPage && renderFooter()}
    </div>
      );
    };
 
    // Check if receiptData has orderInfo
    if (!receiptData?.orderInfo || receiptData.orderInfo.length === 0) {
      return null;
    }

    return (
      <>
        {Array.from({ length: totalPages }).map((_, pageIndex) => renderPage(pageIndex))}
      </>
    );
};


export default PosReceiptTemplate;
